import { useState, useEffect } from "react";
import { withTranslation, TFunction } from "react-i18next";
import { 
  ConsentContainer,
  ConsentContent,
  ConsentTitle,
  ConsentText,
  ButtonGroup,
  ConsentButton
} from "./styles";

interface CookieConsentProps {
  t: TFunction;
  onAccept?: (preferences: ConsentPreferences) => void;
  delay?: number; // Delay in ms before showing the consent popup
}

export interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
}

const CookieConsent = ({ t, onAccept, delay = 1500 }: CookieConsentProps) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Check if user has already set cookie preferences
    const savedPreferences = localStorage.getItem("cookieConsent");
    
    if (!savedPreferences) {
      // Show the consent box after delay if no preferences are saved
      const timer = setTimeout(() => {
        setVisible(true);
      }, delay);
      
      return () => clearTimeout(timer);
    } else if (onAccept) {
      // If preferences exist, parse them and notify parent component
      const parsedPreferences = JSON.parse(savedPreferences) as ConsentPreferences;
      onAccept(parsedPreferences);
    }
  }, [onAccept, delay]);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true
    };
    savePreferences(allAccepted);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false
    };
    savePreferences(necessaryOnly);
  };

  const savePreferences = (prefs: ConsentPreferences) => {
    localStorage.setItem("cookieConsent", JSON.stringify(prefs));
    setVisible(false);
    if (onAccept) {
      onAccept(prefs);
    }
  };

  if (!visible) {
    return null;
  }

  return (
    <ConsentContainer>
      <ConsentContent>
        <ConsentTitle>{t("cookie_consent.title", "Cookie Notice")}</ConsentTitle>
        <ConsentText>
          {t("cookie_consent.description", "We use cookies to enhance your browsing experience. Please choose your cookie preferences below.")}
        </ConsentText>
        
        <ButtonGroup>
          <ConsentButton onClick={handleAcceptAll} primary>
            {t("cookie_consent.accept_all", "Accept All")}
          </ConsentButton>
          
          <ConsentButton onClick={handleAcceptNecessary}>
            {t("cookie_consent.accept_necessary", "Necessary Only")}
          </ConsentButton>
        </ButtonGroup>
      </ConsentContent>
    </ConsentContainer>
  );
};

export default withTranslation()(CookieConsent);